import { useEffect, useMemo, useState } from 'react';
import {
  Star,
  ChevronRight,
  MailQuestion,
} from 'lucide-react';
import useAuthStore from '../../../core/stores/authStore';
import useFavoritesStore from '../../../core/stores/favoritesStore';
import LogUtil from '../../../core/utils/LogUtil';
import { useAdherenceKpi } from '../../hooks/adherence/useAdherenceKpi';
import { useAdherencePatientList } from '../../hooks/adherence/useAdherencePatientList';
import { usePatientDetail } from '../../hooks/patient/usePatientDetail';
import { useAutoSelectFirst } from '../../hooks/ui/useAutoSelectFirst';
import { normalizeGenderLabel } from '../../../core/utils/format';
import MessagePolicyModal from './components/MessagePolicyModal';

const GENDER_MAP = { M: '남', F: '여' };

/* ─────────────────────────────────────────
   상단 KPI 스트립
   ───────────────────────────────────────── */

const KPI_DEFS = [
  { key: 'managed_count',         label: '관리대상 환자',    suffix: '명' },
  { key: 'avg_adherence_percent', label: '평균 복약률',      suffix: '%' },
  { key: 'challenge_count',       label: '챌린지 진행',      suffix: '명', filter: 'challenge' },
  { key: 'churn_risk_count',      label: '중단 위험',        suffix: '명', filter: 'churn_risk' },
  { key: 'churn_call_count',      label: '이탈 (전화 필요)', suffix: '명', filter: 'churned' },
];

const KpiStrip = ({ kpi, activeFilter, onFilter }) => (
  <section className="flex h-[103px] shrink-0 items-center border-b border-[#e4e4e7] bg-white px-6">
    {KPI_DEFS.map(d => {
      const value = kpi?.[d.key] ?? '-';
      const isChurnCall = d.key === 'churn_call_count';
      return (
        <button
          key={d.key}
          type="button"
          disabled={!d.filter}
          aria-pressed={d.filter ? activeFilter === d.filter : undefined}
          onClick={() => d.filter && onFilter(d.filter)}
          className={`flex flex-1 items-center justify-between rounded-lg px-4 py-3 text-left ${activeFilter === d.filter ? 'bg-[#f4f4f5]' : ''} ${d.filter ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <div className="flex flex-col gap-2">
            {/* 디자인 QA 10/23: 라벨 15px SemiBold */}
            <span className="text-15 font-semibold tracking-[-0.3px] text-[#a1a1aa]">{d.label}</span>
            <p className={`text-20 font-semibold leading-[27px] tracking-[-0.4px] tabular-nums ${isChurnCall ? 'text-[#f04351]' : 'text-[#3f3f46]'}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}{d.suffix}
            </p>
          </div>
          {d.filter && <ChevronRight size={20} className="text-[#a1a1aa]" strokeWidth={1.5} />}
        </button>
      );
    })}
  </section>
);

/* ─────────────────────────────────────────
   좌측 환자 리스트 (탭 + 7일 dot grid)
   ───────────────────────────────────────── */

const DOT_CLASS = {
  good:    'bg-[#d4f8cd]',
  warning: 'bg-[#ffe5b2]',
  miss:    'bg-[#ffb6b2]',
  empty:   'border border-[#e4e4e7] bg-white',
};

// status별 % 텍스트 색상
const STATUS_COLOR = {
  normal:     '#34c759',
  poor:       '#ff9500',
  churn_risk: '#f04351',
  expiring:   '#007aff',
  churned:    '#7f1d1d',
  active:     '#34c759',
  unstable:   '#e33e35',
  completed:  '#007aff',
};

// 장기 이행률 % 색 — Figma 15:3255 (중단위험·이탈 #f04351, 저조 #f7a032, 그 외 #3f3f46)
const ADHERENCE_COLOR = {
  poor: '#f7a032',
  churn_risk: '#f04351',
  churned: '#f04351',
};

// 상세 상태 태그 (tag/small/square 80px) — 중단위험은 Figma 15:3274 값
const STATUS_TAG = {
  // 장기 5단계
  normal:     { label: '정상',     classes: 'bg-[#f3fbf5] text-[#34c759]' },  // S1
  poor:       { label: '저조',     classes: 'bg-[#fff7eb] text-[#f79103]' },  // S2
  churn_risk: { label: '복약 중단 위험', classes: 'bg-[#fdecee] text-[#f04351]' },  // S3
  expiring:   { label: '종료임박', classes: 'bg-[#eef2ff] text-[#007aff]' },  // S4 (장기) / 단기 종료임박 공용
  churned:    { label: '이탈',     classes: 'bg-[#7f1d1d] text-white' },      // S5
  // 단기 4단계
  active:     { label: '복용중',   classes: 'bg-[#f3fbf5] text-[#34c759]' },
  unstable:   { label: '복용불안정', classes: 'bg-[#fdecee] text-[#f04351]' },
  completed:  { label: '복용완료', classes: 'bg-[#eef2ff] text-[#007aff]' },
};

// 리스트 상태 텍스트 — Figma 15:3255 (장기) / 56:18841 (단기)
const LIST_STATUS_TAG_LONG = {
  normal: { label: '정상', classes: 'text-[#71717a]' },
  poor: { label: '저조', classes: 'text-[#f7a032]' },
  churn_risk: { label: '중단위험', classes: 'text-[#f04351]' },
  expiring: { label: '종료임박', classes: 'text-[#58a2f9]' },
  churned: { label: '이탈', classes: 'border border-[#f04351] bg-[#fdecee] text-[#f04351]', pill: true },
};
const LIST_STATUS_TAG_SHORT = {
  active: { label: '복용중', classes: 'text-[#58a2f9]' },
  unstable: { label: '복용불안정', classes: 'text-[#f04351]' },
  expiring: { label: '종료임박', classes: 'text-[#f79103]' },
  completed: { label: '복용완료', classes: 'text-[#71717a]' },
};

// icon_element/arrow/check · icon_element/fill/alert (Figma 20px)
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0">
    <path d="M16.0775 4.41081C16.4029 4.08537 16.9304 4.08537 17.2559 4.41081C17.5813 4.73624 17.5813 5.26376 17.2559 5.58919L8.08919 14.7559C7.76376 15.0813 7.23624 15.0813 6.91081 14.7559L2.74414 10.5892C2.4187 10.2638 2.4187 9.73624 2.74414 9.41081C3.06958 9.08537 3.59709 9.08537 3.92253 9.41081L7.5 12.9883L16.0775 4.41081Z" fill="#A1A1AA" />
  </svg>
);
const AlertFillIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M18.3333 10C18.3333 5.3975 14.6025 1.66667 10 1.66667C5.3975 1.66667 1.66667 5.3975 1.66667 10C1.66667 14.6025 5.3975 18.3333 10 18.3333C14.6025 18.3333 18.3333 14.6025 18.3333 10ZM10 5.83333C10.4602 5.83333 10.8333 6.20643 10.8333 6.66667V10.8333C10.8333 11.2936 10.4602 11.6667 10 11.6667C9.53976 11.6667 9.16667 11.2936 9.16667 10.8333V6.66667C9.16667 6.20643 9.53976 5.83333 10 5.83333ZM9.16667 13.3333C9.16667 12.8731 9.53976 12.5 10 12.5H10.0067C10.4669 12.5 10.84 12.8731 10.84 13.3333C10.84 13.7936 10.4669 14.1667 10.0067 14.1667H10C9.53976 14.1667 9.16667 13.7936 9.16667 13.3333Z" fill="#F04351" />
  </svg>
);

const TabButton = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 py-1.5 ${active ? 'border-b-2 border-[#1d356d]' : ''}`}
  >
    <span className={`text-16 tracking-[-0.32px] ${active ? 'text-[#1d356d] font-semibold' : 'text-[#a1a1aa] font-medium'}`}>
      {label}
    </span>
  </button>
);

// table/cell — px12. 고정폭 외 셀은 flex-1 (Figma 장기 80/70.4/148/70.4×4, 단기 80/148/88×4)
const Cell = ({ className = '', children }) => (
  <div className={`flex items-center justify-center px-3 ${className}`}>{children}</div>
);

const TextCell = ({ className, children }) => (
  <Cell className={`flex-1 ${className}`}>
    <p className="min-w-0 flex-1 truncate text-center text-14 tracking-[-0.28px] text-[#71717a]">
      {children}
    </p>
  </Cell>
);

const PatientNameCell = ({ name, gender, age }) => (
  <div className="flex w-[80px] shrink-0 flex-col justify-center gap-0.5 whitespace-nowrap px-3 py-1.5 text-14 tracking-[-0.28px]">
    <p className="truncate font-medium text-[#3f3f46]">{name}</p>
    <p className="truncate text-[#71717a]">{normalizeGenderLabel(gender) || gender} · {age}세</p>
  </div>
);

const WeeklyDots = ({ dots }) => (
  <Cell className="w-[148px] shrink-0 gap-0.5">
    {dots.map((d, i) => (
      <span
        key={i}
        className={`h-4 w-4 shrink-0 ${DOT_CLASS[d] || 'bg-[#e4e4e7]'}`}
      />
    ))}
  </Cell>
);

const StatusTagCell = ({ tag, className }) => (
  <Cell className={`flex-1 ${className}`}>
    {tag && (
      <span className={`inline-flex items-center justify-center whitespace-nowrap rounded-2xl px-2 text-14 font-semibold tracking-[-0.28px] ${tag.pill ? 'py-1' : 'py-0.5'} ${tag.classes}`}>
        {tag.label}
      </span>
    )}
  </Cell>
);

// 병행 단기처방 hover 카드 — Figma 15:3526(복용불안정) / 76:9708(복용 중), 280×182
const SHORT_TERM_HOVER = {
  active:   { label: '복용 중',    Icon: CheckIcon,     titleColor: '#3f3f46', valueColor: '#52525b' },
  unstable: { label: '복용불안정', Icon: AlertFillIcon, titleColor: '#f04351', valueColor: '#f04351' },
};
const HOVER_W = 280;
const HOVER_H = 182;

const formatDays = (v) => (v == null ? '-' : `${v}일`);

const ShortTermHoverCard = ({ shortTerm, rect }) => {
  const meta = SHORT_TERM_HOVER[shortTerm.status];
  const below = rect.bottom + 4;
  const top = below + HOVER_H > window.innerHeight ? rect.top - HOVER_H - 4 : below;
  const disease = shortTerm.disease_name
    ? `${shortTerm.disease_name}${shortTerm.disease_code ? ` (${shortTerm.disease_code})` : ''}`
    : '-';
  const rows = [
    ['최근 3일 중 미복용', shortTerm.missed_days_last3],
    ['잔여일', shortTerm.remaining_days],
    ['연속 미복용', shortTerm.consecutive_missed_days],
  ];
  return (
    <div
      className="pointer-events-none fixed z-50 flex w-[280px] flex-col gap-3 overflow-hidden rounded-[12px] border border-[#e4e4e7] bg-white p-4 shadow-[0px_20px_48px_0px_rgba(0,0,0,0.18)]"
      style={{ left: Math.max(8, rect.right - HOVER_W), top }}
    >
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center gap-1">
          <meta.Icon />
          <p className="text-16 font-semibold tracking-[-0.32px]" style={{ color: meta.titleColor }}>{meta.label}</p>
        </div>
        <div className="flex gap-3 border-b border-[#e4e4e7] pb-3 text-14 tracking-[-0.28px]">
          <span className="whitespace-nowrap font-medium text-[#71717a]">질병</span>
          <p className="min-w-0 flex-1 truncate font-semibold text-[#3f3f46]">{disease}</p>
        </div>
      </div>
      <div className="grid w-full grid-cols-[100px_minmax(0,1fr)] gap-x-3 gap-y-2 text-14 tracking-[-0.28px]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <span className="whitespace-nowrap font-medium text-[#a1a1aa]">{label}</span>
            <span className="text-right font-semibold" style={{ color: meta.valueColor }}>{formatDays(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// 장기 탭 "단기" 컬럼 = 병행 단기처방 상태 (row.short_term, 명세 v2 C-1). 없으면 아이콘·hover 없음.
const ShortTermCell = ({ shortTerm }) => {
  const [rect, setRect] = useState(null);
  const meta = shortTerm && SHORT_TERM_HOVER[shortTerm.status];
  return (
    <Cell className="min-w-[54px] flex-1">
      {meta && (
        <span
          className="flex"
          onMouseEnter={(e) => setRect(e.currentTarget.getBoundingClientRect())}
          onMouseLeave={() => setRect(null)}
        >
          <meta.Icon />
          {rect && <ShortTermHoverCard shortTerm={shortTerm} rect={rect} />}
        </span>
      )}
    </Cell>
  );
};

// 단기 탭 마지막 컬럼 — Figma 56:18841: 복용완료 ✓ / 복용중 !
const ShortTabIconCell = ({ status }) => (
  <Cell className="min-w-[54px] flex-1">
    {status === 'completed' && <CheckIcon />}
    {status === 'active' && <AlertFillIcon />}
  </Cell>
);

const HeaderCell = ({ className = '', align = 'text-center', children }) => (
  <Cell className={className}>
    <p className={`min-w-0 flex-1 truncate ${align}`}>{children}</p>
  </Cell>
);

const PatientList = ({ patients, type, setType, onSelect, loading }) => {
  const isLong = type === 'long';
  return (
    <section className="flex min-w-[580px] basis-[51.14%] flex-none flex-col border-r border-[#e4e4e7] bg-white px-6 pt-6">
      {/* 탭 헤더 */}
      <div className="flex h-[34px] items-start gap-6">
        <TabButton active={isLong}  label="장기처방환자" onClick={() => setType('long')} />
        <TabButton active={!isLong} label="단기처방환자" onClick={() => setType('short')} />
      </div>

      {/* 컬럼 헤더 — R14 #3f3f46, px12 */}
      <div className="mt-6 flex h-10 items-stretch border-y border-[#e4e4e7] bg-[#f4f4f5] text-14 tracking-[-0.28px] text-[#3f3f46]">
        <HeaderCell className="w-[80px] shrink-0" align="text-left">환자</HeaderCell>
        {isLong && <HeaderCell className="min-w-[60px] flex-1" align="text-right">이행률</HeaderCell>}
        <HeaderCell className="w-[148px] shrink-0">{isLong ? '최근 7일' : '최근 복약기록'}</HeaderCell>
        <HeaderCell className="min-w-[70px] flex-1">{isLong ? '일 N회' : '복용횟수'}</HeaderCell>
        <HeaderCell className={`${isLong ? 'min-w-[64px]' : 'min-w-[70px]'} flex-1`}>{isLong ? '일차' : '잔여일수'}</HeaderCell>
        <HeaderCell className={`${isLong ? 'min-w-[68px]' : 'min-w-[74px]'} flex-1`} align={isLong ? 'text-left' : 'text-center'}>{isLong ? '관리상태' : '복용상태'}</HeaderCell>
        <HeaderCell className="min-w-[54px] flex-1">단기</HeaderCell>
      </div>

      {/* 환자 리스트 */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading || patients.length === 0 ? null : (
          patients.map(p => {
            const tag = (isLong ? LIST_STATUS_TAG_LONG : LIST_STATUS_TAG_SHORT)[p.status] || null;
            const dots = p.weekly || [];
            return (
              <div
                key={p.user_id}
                onClick={() => onSelect(p.user_id)}
                className="flex h-[54px] cursor-pointer items-stretch border-b border-[#e4e4e7] transition-colors hover:bg-[#fafafa]"
              >
                <PatientNameCell name={p.name} gender={p.gender} age={p.age} />

                {isLong && (
                  <Cell className="min-w-[60px] flex-1">
                    <p className="min-w-0 flex-1 truncate text-right text-14 font-semibold tracking-[-0.28px]" style={{ color: ADHERENCE_COLOR[p.status] || '#3f3f46' }}>
                      {p.adherence != null ? `${p.adherence}%` : '-'}
                    </p>
                  </Cell>
                )}

                <WeeklyDots dots={dots} />

                <TextCell className="min-w-[70px]">{(isLong ? p.frequency : p.dose_label) ?? '-'}</TextCell>
                <TextCell className={isLong ? 'min-w-[64px]' : 'min-w-[70px]'}>{(isLong ? p.day_label : p.remaining_label) ?? '-'}</TextCell>

                <StatusTagCell tag={tag} className={isLong ? 'min-w-[68px]' : 'min-w-[74px]'} />

                {isLong ? <ShortTermCell shortTerm={p.short_term} /> : <ShortTabIconCell status={p.status} />}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   우측 환자 상세
   ───────────────────────────────────────── */

// "2026-04-27" → "2026.04.27"
const formatVisitDate = (raw) => (raw ? String(raw).slice(0, 10).replace(/-/g, '.') : '');

// patient — Figma 15:3264 (76:17509)
const PatientHeader = ({ detail }) => {
  const favoriteUserIds = useFavoritesStore(s => s.userIds);
  const toggleFavoriteAction = useFavoritesStore(s => s.toggle);
  const pharmacyBrn = useAuthStore(s => s.pharmacy?.business_registration_number);
  const uid = detail.user_id ?? detail.id;
  const isFavorite = detail.is_favorite || (uid != null && favoriteUserIds.includes(uid));
  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (!uid || !pharmacyBrn) return;
    toggleFavoriteAction(pharmacyBrn, uid);
  };

  return (
    <div className="flex h-[67px] w-full flex-col gap-1 border-b border-[#e4e4e7] pb-4">
      <div className="flex min-w-0 items-center gap-1">
        <h2 className="truncate text-20 font-semibold tracking-[-0.4px] text-[#18181b]">
          {detail.name}
        </h2>
        <button type="button" onClick={handleToggleFavorite} className="flex h-5 w-5 shrink-0 items-center justify-center" aria-label={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}>
          <Star size={18} className={isFavorite ? 'fill-[#ffb02a] text-[#ffb02a]' : 'text-[#d4d4d8]'} strokeWidth={1.5} />
        </button>
      </div>
      <div className="flex items-start gap-3 whitespace-nowrap text-14 font-medium tracking-[-0.28px]">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex items-center gap-0.5 text-[#52525b]">
            <span>{normalizeGenderLabel(detail.gender) || detail.gender}</span>
            <span className="text-[#bcbcc2]">·</span>
            <span>{detail.age}세</span>
          </div>
          {detail.last_visit && (
            <div className="flex items-center gap-1.5">
              <span className="text-[#bcbcc2]">|</span>
              <span className="flex items-center gap-1 text-[#a1a1aa]"><span>최근방문</span><span>{formatVisitDate(detail.last_visit)}</span></span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#71717a]">{detail.phone_number || '-'}</span>
          {detail.phone_number && (
            <button type="button" onClick={() => navigator.clipboard?.writeText(detail.phone_number)} className="text-[#007aff]">복사</button>
          )}
        </div>
      </div>
    </div>
  );
};

// icon_element/fill/question 16px #d4d4d8
const QuestionFillIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="shrink-0">
    <path d="M8 1.33333C11.682 1.33333 14.6667 4.318 14.6667 8C14.6667 11.682 11.682 14.6667 8 14.6667C4.318 14.6667 1.33333 11.682 1.33333 8C1.33333 4.318 4.318 1.33333 8 1.33333ZM8 10.6667C7.63181 10.6667 7.33333 10.9651 7.33333 11.3333C7.33333 11.7015 7.63181 12 8 12C8.36819 12 8.66667 11.7015 8.66667 11.3333C8.66667 10.9651 8.36819 10.6667 8 10.6667ZM8 4.33333C6.66532 4.33333 5.58333 5.41532 5.58333 6.75C5.58333 7.11819 5.88181 7.41667 6.25 7.41667C6.61819 7.41667 6.91667 7.11819 6.91667 6.75C6.91667 6.15169 7.40169 5.66667 8 5.66667C8.59831 5.66667 9.08333 6.15169 9.08333 6.75C9.08333 7.20529 8.80245 7.59524 8.40333 7.75667C7.95267 7.93667 7.33333 8.398 7.33333 9.16667V9.33333C7.33333 9.70152 7.63181 10 8 10C8.36819 10 8.66667 9.70152 8.66667 9.33333C8.66667 9.17067 8.7 9.08933 8.84067 9.02L8.89867 8.99333C9.78478 8.63687 10.4167 7.76694 10.4167 6.75C10.4167 5.41532 9.33468 4.33333 8 4.33333Z" fill="#D4D4D8" />
  </svg>
);

// medication adherence + Card — Figma 15:3267 (장기, gap 24) / 15:3408 (단기, % 없음, gap 16)
const AdherenceOverview = ({ detail, isLong }) => (
  <div className="-mt-2 flex flex-col gap-8">
    <div className={`flex flex-col ${isLong ? 'gap-6' : 'gap-4'}`}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2.5">
          <h3 className="min-w-0 flex-1 text-16 font-semibold tracking-[-0.32px] text-[#0c151f]">복약이행률</h3>
          <div className="flex gap-2">
            {detail.challenge_in_progress && <span className="flex w-[80px] items-center justify-center whitespace-nowrap rounded bg-[#efeff1] px-1.5 py-0.5 text-12 font-semibold text-[#3f3f46]">챌린지 진행 중</span>}
            {STATUS_TAG[detail.status] && <span className={`flex w-[80px] items-center justify-center whitespace-nowrap rounded px-1.5 py-0.5 text-12 font-semibold ${STATUS_TAG[detail.status].classes}`}>{STATUS_TAG[detail.status].label}</span>}
          </div>
        </div>
        {isLong && (
          <div className="flex items-end gap-2 whitespace-nowrap">
            <strong className="text-30 font-bold tracking-[-0.9px]" style={{ color: STATUS_COLOR[detail.status] || '#3f3f46' }}>{detail.adherence_percent != null ? `${detail.adherence_percent}%` : '-'}</strong>
            {detail.dose_kpi_raw && <span className="flex gap-0.5 pb-[5px] text-12 font-medium text-[#71717a]"><span>{detail.dose_kpi_raw.total_taken_count ?? '-'}회</span><span>/</span><span>{detail.dose_kpi_raw.total_prescription_count ?? '-'}회</span></span>}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6 rounded-[10px] bg-white p-4">
        <div className="flex gap-5">
          {(detail.dose_kpi || []).map((k, idx) => (
            <div key={idx} className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate text-12 font-semibold text-[#a1a1aa]">{k.label}</span>
              <strong className={`text-18 font-semibold tracking-[-0.36px] ${idx === 1 ? 'text-[#f04351]' : 'text-[#3f3f46]'}`}>{k.value ?? '-'}{k.value != null && (k.unit || '회')}</strong>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-1 text-12 font-medium text-[#3f3f46]">최근 7일 복약 기록 <QuestionFillIcon /></div>
          <div className="flex gap-1">
            {(detail.weekly_calendar || []).map((d, idx) => (
              <div key={idx} className={`flex h-8 min-w-0 flex-1 items-center justify-center rounded py-[5px] text-12 font-semibold text-[#52525b] ${DOT_CLASS[d.state] || 'bg-[#f4f4f5]'}`}>{d.date}</div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Alert Container — Figma 15:3324 */}
    {detail.allergy_info && detail.allergy_info !== '없음' && (
      <div className="flex gap-1 rounded-lg bg-[#fef4f5] p-4">
        <span className="pt-px"><AlertFillIcon size={16} /></span>
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-12 ">
          <strong className="font-semibold text-[#27272a]">알레르기·부작용 이력</strong>
          <span className="text-[#52525b]">{detail.allergy_info}</span>
        </div>
      </div>
    )}
  </div>
);

// 처방정보 요약 — Figma 15:3325
const PrescriptionSummary = ({ prescription }) => {
  if (!prescription) return null;
  const Row = ({ label, children, tall = false }) => (
    <div className={`flex border-b border-[#e4e4e7] bg-white ${tall ? '' : 'h-7'}`}>
      <div className="flex w-[100px] shrink-0 items-center bg-[#f4f4f5] px-2 text-14 font-medium tracking-[-0.28px] text-[#71717a]">{label}</div>
      <div className="flex min-w-0 flex-1 flex-col justify-center text-14 tracking-[-0.28px] text-[#27272a]">{children}</div>
    </div>
  );
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-16 font-semibold tracking-[-0.32px] text-[#0c151f]">처방정보 요약</h3>
      <div className="border-t border-[#e4e4e7]">
        <Row label="질병명"><span className="truncate px-2">{prescription.diagnosis || '-'}</span></Row>
        <Row label="약물 계열"><span className="truncate px-2">{prescription.atc_code || '-'}</span></Row>
        <Row label="복약 정보"><span className="truncate px-2">{prescription.dosing || '-'}</span></Row>
        <Row label="조제 정보" tall>
          {(prescription.medications || []).map((m, idx) => <span key={idx} className="flex h-7 items-center truncate border-b border-[#e2e8f0] px-2 last:border-b-0">{m.name}{m.schedule ? ` (${m.schedule})` : ''}</span>)}
        </Row>
      </div>
    </div>
  );
};

// message_type → 유형 라벨 (title 없을 때만 표시. 가짜 본문 생성 금지)
const MESSAGE_TYPE_LABEL = {
  L_START: '복약 시작 안내',
  L_LOW: '복약 미응답 안내',
  L_LOW_DAY5: '복약 중단 위험 안내',
  L_END: '복약 기간 종료 안내',
  L_END_REVISIT: '재방문 안내',
  L_REVISIT: '재방문 안내',
  L_LONG_MANAGE: '장기 관리 안내',
  L_THANKS: '감사 인사',
  S_START: '단기 복약 시작',
  S_END: '단기 복약 종료',
  S_REVISIT: '단기 재방문 안내',
  S_MID_CHECK: '중간 점검',
};
const messageTypeLabel = (type) => {
  if (!type) return null;
  const code = Object.keys(MESSAGE_TYPE_LABEL)
    .filter(c => type === c || type.startsWith(`${c}_`))
    .sort((x, y) => y.length - x.length)[0];
  return code ? MESSAGE_TYPE_LABEL[code] : type;
};

const formatSentAt = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}.${dd} ${hh}:${mi}`;
};

// recent messages — Figma 15:3357
const MessageHistoryCard = ({ message_history = [], onShowGuide }) => (
  <div className="flex w-full flex-col gap-3">
    <header className="flex items-center justify-between w-full">
      <h3 className="text-16 tracking-[-0.32px] font-semibold text-[#0c151f]">
        최근 메시지 발송 이력
      </h3>
      <button
        type="button"
        onClick={onShowGuide}
        className="inline-flex items-center justify-center gap-1 rounded-md border border-[#e4e4e7] bg-white px-1.5 py-1 hover:bg-[#fafafa]"
      >
        <MailQuestion size={16} className="text-[#3f3f46]" strokeWidth={1.75} />
        <span className="whitespace-nowrap text-14 font-semibold tracking-[-0.28px] text-[#27272a]">메시지 발송기준 보기</span>
      </button>
    </header>
    <div className="w-full rounded-[10px] bg-white p-4">
      <ul className="flex flex-col gap-3 w-full">
        {message_history.map((m, idx) => {
          const last = idx === message_history.length - 1;
          const sentAt = formatSentAt(m.sent_at) || m.date || '';
          // 발송 문구: title(알림톡 템플릿 이름) → 없으면 유형 라벨. 단독 발송이면 접미
          const body = `${m.title || messageTypeLabel(m.message_type) || '-'}${m.is_exclusive ? ' (단독 발송)' : ''}`;
          // tag_label은 백엔드 정의 대기(⏸) — 값 없으면 숨김
          const tagLabel = m.tag_label;
          return (
            <li
              key={m.id ?? idx}
              className={`flex h-[50px] shrink-0 items-center gap-4 pb-2 ${last ? '' : 'border-b border-[#efeff1]'}`}
            >
              <div className="flex flex-1 min-w-0 flex-col gap-0.5 justify-center">
                <p className="truncate text-14 tracking-[-0.28px] text-[#71717a]">{sentAt}</p>
                <p className="truncate text-14 font-medium tracking-[-0.28px] text-[#3f3f46]">{body}</p>
              </div>
              {tagLabel && (
                <span className="inline-flex items-center justify-center whitespace-nowrap shrink-0 rounded bg-[#eff1f3] px-1.5 py-0.5 text-14 font-medium tracking-[-0.28px] text-[#3f3f46]">
                  {tagLabel}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  </div>
);

const PatientAdherenceDetail = ({ detail, isLong, onShowMessageGuide }) => {
  if (!detail) {
    return (
      <section className="flex-1 min-w-0 flex items-center justify-center">
        <p className="text-14 text-[#a1a1aa]">왼쪽에서 환자를 선택하세요</p>
      </section>
    );
  }

  return (
    <section className="min-w-[552px] basis-[48.86%] flex-none overflow-y-auto bg-[#fafafa] px-6 py-6">
      <div className="flex w-full flex-col gap-8">
        <PatientHeader detail={detail} />

        <AdherenceOverview detail={detail} isLong={isLong} />

        <PrescriptionSummary prescription={detail.prescription} />

        <MessageHistoryCard
          message_history={detail.message_history ?? []}
          onShowGuide={onShowMessageGuide}
        />
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   AdherencePage (메인)
   ───────────────────────────────────────── */

const AdherencePage = () => {
  const pharmacy = useAuthStore(s => s.pharmacy);
  const brn = pharmacy?.business_registration_number;

  const [type, setType] = useState('long');
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  // 데이터 fetch는 hook으로 (캐싱 + 자동 loading/error)
  const kpiQuery = useAdherenceKpi(brn);
  const longQuery = useAdherencePatientList(brn, 'long');
  const shortQuery = useAdherencePatientList(brn, 'short');
  const detailQuery = usePatientDetail(selectedId, type);

  // 디자인/기능 동일 유지 — 기존 변수명 그대로 매핑
  const kpi = kpiQuery.data ?? null;
  const allPatients = (type === 'long' ? longQuery.data?.patients : shortQuery.data?.patients) ?? [];
  const patients = useMemo(() => {
    if (activeFilter === 'challenge') return allPatients.filter(patient => patient.challenge_in_progress);
    if (activeFilter) return allPatients.filter(patient => patient.status === activeFilter);
    return allPatients;
  }, [activeFilter, allPatients]);
  const listLoading = longQuery.isLoading || shortQuery.isLoading;
  // detail 응답엔 challenge_in_progress가 없어 리스트 행(dosage/long·short) 값 사용
  const selectedRow = allPatients.find(p => p.user_id === selectedId);
  const detail = detailQuery.data
    ? { ...detailQuery.data, challenge_in_progress: !!selectedRow?.challenge_in_progress }
    : null;

  // 에러 로깅 (기존 동작 유지)
  useEffect(() => { if (kpiQuery.error) LogUtil.error('[AdherencePage] kpi 실패:', kpiQuery.error); }, [kpiQuery.error]);
  useEffect(() => { if (longQuery.error) LogUtil.error('[AdherencePage] patients(long) 실패:', longQuery.error); }, [longQuery.error]);
  useEffect(() => { if (shortQuery.error) LogUtil.error('[AdherencePage] patients(short) 실패:', shortQuery.error); }, [shortQuery.error]);
  useEffect(() => { if (detailQuery.error) LogUtil.error('[AdherencePage] detail 실패:', detailQuery.error); }, [detailQuery.error]);

  // 첫 환자 자동 선택 (UI hook으로 분리)
  useAutoSelectFirst(patients, selectedId, setSelectedId, (p) => p.user_id);

  const handleFilter = (filter) => {
    setActiveFilter(current => current === filter ? null : filter);
    if (filter !== 'challenge') setType('long');
    setSelectedId(null);
  };

  const handleTypeChange = (nextType) => {
    setType(nextType);
    setActiveFilter(null);
    setSelectedId(null);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      <KpiStrip kpi={kpi} activeFilter={activeFilter} onFilter={handleFilter} />

      <div className="flex flex-1 min-h-0">
        <PatientList
          patients={patients}
          type={type}
          setType={handleTypeChange}
          onSelect={setSelectedId}
          loading={listLoading}
        />
        <PatientAdherenceDetail
          detail={detail}
          isLong={type === 'long'}
          onShowMessageGuide={() => setShowPolicyModal(true)}
        />
      </div>

      <MessagePolicyModal
        open={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
      />
    </div>
  );
};

export default AdherencePage;
